import type { EquipAreaViewDto } from "@ckweb/ckfetch/connectables/equipmentAreaManagement";
import type { SkillTypeDto } from "@ckweb/ckfetch/connectables/employeeManagement";
import type { GridConfig } from "@/types/grid";
import type { BulkImportPageSetupStatic } from "@/types/bulk-import";

export const getDailyDutiesPageConfig =
  (): BulkImportPageSetupStatic<DailyDutySchema> => ({
    id: "daily-duties",
    pageTitle: "cksetup.imports.daily-duties.pageTitle",
    pageHeading: "cksetup.imports.daily-duties.heading",
    learnMoreTitle: "cksetup.imports.daily-duties.learnMoreTitle",
    learnMoreText: "cksetup.imports.daily-duties.learnMoreText",
    templateFileName: "cksetup.imports.daily-duties.templateName",
    templateWorksheetName: "tableColumns.dailyDuties.worksheetName",
    tabTitle: "cksetup.imports.bulk.dailyDutiesHeading",
    tabDescription: "cksetup.imports.bulk.dailyDutiesDescription",
    imageSrc: "/static/images/bulk/daily-duties.png",
    tabTooltip: "cksetup.imports.bulk.dailyDutiesTooltip",
    validationSchema: dailyDutySchema,
    addButtonLabel: "cksetup.imports.daily-duties.addNewDailyDutyLine",
  });

export const getDailyDutiesGridConfig = async ({
  equipmentAreas,
  skillTypes,
}: {
  equipmentAreas: EquipAreaViewDto[];
  skillTypes: SkillTypeDto[];
}): Promise<GridConfig<DailyDutySchema>> => {
  const defaultRowData = {
    active: true,
    forecastMinutes: 0,
    pdfName: "",
    equipName: "",
  };
  return {
    computeItemFn: (item, { rowIndex }) => {
      const equip =
        $ck._.isNil(rowIndex) &&
        $ck._.isNil(item.equip) &&
        !item.$touched?.equip
          ? equipmentAreas[0]?.number
          : item.equip;
      const equipmentArea = equipmentAreas?.find(
        (equipmentArea) =>
          equipmentArea.active && equipmentArea.number === equip,
      );
      const skill =
        $ck._.isNil(rowIndex) &&
        $ck._.isNil(item.skill) &&
        !item.$touched?.skill
          ? skillTypes[0]?.code
          : item.skill;
      return {
        ...defaultRowData,
        ...item,
        equip,
        skill,
        forecastMinutes: item.forecastMinutes ?? 0,
        equipName: equipmentArea?.name ?? "",
      };
    },
    defaultRowData,
    columns: [
      {
        field: "number",
        alternativeMappingNames: ["id"],
        hidden: true,
        isKey: true,
        propagateErrorsTo: "name",
      },
      {
        field: "name",
        title: "tableColumns.dailyDuties.name",
        helperText: "tableColumns.dailyDuties.nameHelperText",
        placeholder: "tableColumns.enterName",
        editor: "text",
      },
      {
        field: "active",
        title: "tableColumns.active",
        helperText: "tableColumns.dailyDuties.activeHelperText",
        editor: "boolean",
      },
      {
        field: "forecastMinutes",
        title: "tableColumns.dailyDuties.forecastMinutes",
        helperText: "tableColumns.dailyDuties.forecastMinutesHelperText",
        placeholder: "tableColumns.dailyDuties.enterMinutes",
        editor: "numeric",
        minWidthPx: 200,
      },
      {
        field: "equip",
        title: "tableColumns.dailyDuties.equipment",
        helperText: "tableColumns.dailyDuties.equipmentHelperText",
        placeholder: "tableColumns.dailyDuties.select",
        editor: "select",
        editorProps: {
          items: equipmentAreas
            .filter(({ active }) => active)
            .map((item) => ({
              label: item.name,
              value: item.number,
            })),
        },
        minWidthPx: 140,
      },
      {
        field: "equipName",
        hidden: true,
        propagateErrorsTo: "equip",
      },
      {
        field: "pdfName",
        hidden: true,
      },
      {
        field: "skill",
        title: "tableColumns.dailyDuties.skill",
        helperText: "tableColumns.dailyDuties.skillHelperText",
        placeholder: "tableColumns.dailyDuties.select",
        editor: "select",
        default: "",
        editorProps: {
          translateItems: true,
          items: [
            { label: "formFields.none", value: "" },
            ...skillTypes.map((item) => ({
              label: item.name,
              value: item.code,
            })),
          ],
        },
      },
    ],
  };
};
